import { WillowClient, ConsensusClient, signEd25519 } from '@willow/sdk';

// did:willow:validator1 credentials.
//
// This is the RFC 8032 Section 7.1 "Test 1" Ed25519 test vector — a publicly
// known keypair also used by the Rust SDK's devnet helpers at
// sdk/willow-rust/src/lib.rs:166. In every Willow devnet genesis, this DID is
// pre-registered and funded with ~200k WILL tokens, so it can sign writes
// (register subgroves, store data) without any bootstrap dance.
//
// NEVER use this keypair in production. It's intentionally public.
export const DEVNET_VALIDATOR1 = {
  did: 'did:willow:validator1',
  privateKey: '9d61b19deffd5a60ba844af492ec2cc44449c5697b326919703bac031cae7f60',
  publicKey: 'd75a980182b10ab7d54bfed3c964073a0ee172f3daa62325af021a68f707511a',
  publicKeyId: 'did:willow:validator1#key-1',
} as const;

let cached: WillowClient | null = null;
let cachedKey: string | null = null;

/// CometBFT RPC URL the dashboard talks to. In local dev we route through
/// the Vite proxy path `/cometbft-rpc` (vite.config.ts forwards it to
/// rpc.willow.tech server-side) so the browser request stays same-origin —
/// rpc.willow.tech's CORS allowlist admits only the deployed dashboard
/// origin, not http://127.0.0.1:5173. The production build hits the absolute
/// URL directly, which is CORS-allowed for that origin.
const DEFAULT_CONSENSUS_RPC = import.meta.env.DEV
  ? '/cometbft-rpc'
  : 'https://rpc.willow.tech';

/// Module-scoped override seeded to the default so the very first fetch on
/// page load uses it before ConnectionProvider's effect pushes it back in.
let consensusUrlOverride: string | null = DEFAULT_CONSENSUS_RPC;

export function setConsensusUrlOverride(url: string | null) {
  const normalized = url && url.length > 0 ? url : null;
  if (normalized === consensusUrlOverride) return;
  consensusUrlOverride = normalized;
  // Override changed → cached clients are pointing at the wrong RPC.
  cached = null;
  cachedKey = null;
  cachedConsensus = null;
  cachedConsensusUrl = null;
}

/// Public accessor for the resolved consensus URL — needed by panels that
/// build their own URLs (e.g. StatePanel hits /status directly) instead of
/// going through ConsensusClient.
export function resolveConsensusUrlFor(apiUrl: string): string {
  return resolveConsensusUrl(apiUrl);
}

/// Resolve the CometBFT RPC URL for the given validator URL.
///
/// Order of preference:
///   1. Explicit `consensusUrl` passed by the caller.
///   2. Module-level override set by `setConsensusUrlOverride`.
///   3. URLs with a port (e.g. devnet `http://localhost:3031`): derive the
///      matching RPC port (3031 → 26657, 3032 → 26757, ...).
///   4. URLs without a port (e.g. `https://api.willow.tech`): fall back to
///      the Vite dev proxy `/cometbft-rpc`. Production users hosting the
///      RPC at a separate hostname (e.g. `https://rpc.willow.tech`) must
///      set the override or pass `consensusUrl` explicitly — there's no
///      reliable way to derive a hostname-based RPC URL from a
///      hostname-based API URL.
function resolveConsensusUrl(apiUrl: string, consensusUrl?: string): string {
  if (consensusUrl) return consensusUrl;
  if (consensusUrlOverride) return consensusUrlOverride;
  const match = apiUrl.match(/:(\d+)(\/)?$/);
  if (match) {
    const apiPort = parseInt(match[1]);
    const nodeN = apiPort - 3030;
    const rpcPort = 26557 + nodeN * 100;
    return apiUrl.replace(`:${apiPort}`, `:${rpcPort}`);
  }
  return '/cometbft-rpc';
}

export function getClient(
  apiUrl: string,
  indexerUrl?: string,
  consensusUrl?: string,
): WillowClient {
  const key = `${apiUrl}|${indexerUrl ?? ''}|${consensusUrl ?? ''}`;
  if (cached && cachedKey === key) return cached;

  const cometUrl = resolveConsensusUrl(apiUrl, consensusUrl);
  const client = new WillowClient({
    apiUrl,
    indexerUrl: indexerUrl || undefined,
    consensusRpcUrl: cometUrl,
  });
  client.auth.setIdentity(
    DEVNET_VALIDATOR1.did,
    DEVNET_VALIDATOR1.privateKey,
    DEVNET_VALIDATOR1.publicKeyId,
  );

  cached = client;
  cachedKey = key;
  return client;
}

let cachedConsensus: ConsensusClient | null = null;
let cachedConsensusUrl: string | null = null;

export function getConsensusClient(apiUrl: string, consensusUrl?: string): ConsensusClient {
  const cometUrl = resolveConsensusUrl(apiUrl, consensusUrl);

  if (cachedConsensus && cachedConsensusUrl === cometUrl) return cachedConsensus;

  cachedConsensus = new ConsensusClient({
    consensusRpcUrl: cometUrl,
    apiUrl,
    chainId: 'test-chain-consensus',
    maxRetries: 3,
    requestTimeoutSecs: 30,
    retryDelaySecs: 1,
  });
  cachedConsensusUrl = cometUrl;
  return cachedConsensus;
}

export function sign(message: string, privateKey: string): string {
  return signEd25519(message, privateKey);
}

/// Default URLs.
///
/// API and indexer point at relative paths so requests go through the same
/// origin as the dashboard (dashboard.willow.tech in prod, vite dev server
/// locally). nginx in prod / vite.config in dev proxies these to willow-saas,
/// which authenticates the session cookie + forwards to the validator or
/// indexer. Going same-origin is what lets the HttpOnly session cookie
/// flow without CORS-with-credentials shenanigans.
///
/// Devnet users can change in the Connection popover; their changes persist
/// for the session.
export const DEFAULT_API_URL = '/api';
export const DEFAULT_INDEXER_URL = '/indexer';
export const DEFAULT_CONSENSUS_URL = DEFAULT_CONSENSUS_RPC;
